import { EmojiStyle, Theme } from 'emoji-picker-react';
import { Smile } from 'lucide-react';
import {
  Component,
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

const PICKER_WIDTH = 320;
const PICKER_HEIGHT = 380;
const VIEWPORT_GAP = 8;

type EmojiPickerPopoverProps = {
  onSelect: (emoji: string) => void;
  disabled?: boolean;
  variant?: 'light' | 'dark';
};

type PickerErrorBoundaryProps = {
  children: ReactNode;
  onRetry: () => void;
};

type PickerErrorBoundaryState = {
  hasError: boolean;
};

class PickerErrorBoundary extends Component<PickerErrorBoundaryProps, PickerErrorBoundaryState> {
  state: PickerErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): PickerErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Emoji picker failed to load', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex flex-col items-center justify-center gap-3 px-4 text-center text-sm text-gray-600"
          style={{ width: PICKER_WIDTH, height: PICKER_HEIGHT }}
        >
          <p>Emoji picker failed to load.</p>
          <button
            type="button"
            onClick={this.props.onRetry}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export function EmojiPickerPopover({
  onSelect,
  disabled,
  variant = 'light',
}: EmojiPickerPopoverProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);
  const [position, setPosition] = useState<{
    left: number;
    bottom?: number;
    top?: number;
  } | null>(null);

  // Recreate lazy() on retry so a rejected dynamic import is not stuck cached.
  const EmojiPicker = useMemo(() => lazy(() => import('emoji-picker-react')), [pickerKey]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    function measure() {
      const el = buttonRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const left = Math.max(
        VIEWPORT_GAP,
        Math.min(rect.left, window.innerWidth - PICKER_WIDTH - VIEWPORT_GAP)
      );
      const spaceAbove = rect.top - VIEWPORT_GAP;
      const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_GAP;
      const placeAbove = spaceAbove >= PICKER_HEIGHT || spaceAbove >= spaceBelow;

      if (placeAbove) {
        setPosition({
          left,
          bottom: window.innerHeight - rect.top + 4,
        });
      } else {
        setPosition({
          left,
          top: rect.bottom + 4,
        });
      }
    }
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setIsOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  const buttonClass =
    variant === 'dark'
      ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700';

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        title="Add emoji"
        aria-label="Add emoji"
        aria-expanded={isOpen}
        onMouseDown={e => e.preventDefault()}
        onClick={() => setIsOpen(open => !open)}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${buttonClass}`}
      >
        <Smile className="h-4 w-4" />
      </button>

      {isOpen &&
        position &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={panelRef}
            data-emoji-picker
            style={{
              position: 'fixed',
              left: position.left,
              ...(position.top !== undefined ? { top: position.top } : {}),
              ...(position.bottom !== undefined ? { bottom: position.bottom } : {}),
              zIndex: 50,
            }}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
            onMouseDown={e => e.preventDefault()}
          >
            <PickerErrorBoundary key={pickerKey} onRetry={() => setPickerKey(key => key + 1)}>
              <Suspense
                fallback={
                  <div
                    className="flex items-center justify-center text-sm text-gray-500"
                    style={{ width: PICKER_WIDTH, height: PICKER_HEIGHT }}
                  >
                    Loading emojis…
                  </div>
                }
              >
                <EmojiPicker
                  open
                  width={PICKER_WIDTH}
                  height={PICKER_HEIGHT}
                  emojiStyle={EmojiStyle.APPLE}
                  theme={variant === 'dark' ? Theme.DARK : Theme.LIGHT}
                  lazyLoadEmojis
                  autoFocusSearch={false}
                  searchPlaceholder="Search emojis"
                  previewConfig={{ showPreview: false }}
                  onEmojiClick={emojiData => {
                    onSelect(emojiData.emoji);
                    setIsOpen(false);
                  }}
                />
              </Suspense>
            </PickerErrorBoundary>
          </div>,
          document.body
        )}
    </>
  );
}
